import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import "./ZincoDashboard.css";
import axios from "axios";
import { format } from "date-fns";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Swal from 'sweetalert2';
import { useUser } from '../context/UserContext';
import { fireAudit } from '../utils/auditClient';

const ZincoDashboard = () => {
  const [academy, setAcademy] = useState("");
  const { userData } = useUser();
  const [court, setCourt] = useState("");
  const [academies, setAcademies] = useState([]);
  const [courts, setCourts] = useState([]);
  const [allData, setAllData] = useState([]);
  const { selectedCenter } = useUser();
  const [fromTimeFilter, setFromTimeFilter] = useState(null);
  const [toTimeFilter, setToTimeFilter] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const API_BASE_URL = process.env.REACT_APP_API_ACADEMIES;
  const API_KEY = process.env.REACT_APP_API_KEY;
  const baselineRef = useRef(null);

  useEffect(() => {
    const headers = { 'x-api-key': API_KEY };
    axios
      .get(API_BASE_URL, { headers })
      .then((response) => {
        const { academiesCourtsAndArenas } = response.data;
        setAllData(academiesCourtsAndArenas);
        let filteredAcademies = academiesCourtsAndArenas;
        if (userData?.userType === 'ACADEMY' && Array.isArray(userData.academyIds)) {
          filteredAcademies = academiesCourtsAndArenas.filter((item) =>
            userData.academyIds.includes(item.academy_id)
          );
        }
        const academyOptions = filteredAcademies.map((item) => ({
          value: item.academy_id,
          label: item.academy_name,
        }));
        setAcademies(academyOptions);
      })
      .catch((error) => {
        console.error("Error fetching academies and courts", error);
      });
  }, [userData]);

  const handleAcademyChange = (selectedOption) => {
    setAcademy(selectedOption);
    setCourt(null);
    const selectedAcademyData = allData.find(
      (item) => item.academy_id === selectedOption.value
    );
    if (selectedAcademyData) {
      const courtNames = selectedAcademyData.court_names.split(",");
      const courtIds = selectedAcademyData.court_ids.split(",");
      const courtOptions = courtNames.map((court, index) => ({
        value: courtIds[index].trim(),
        label: court.trim(),
      }));
      setCourts(courtOptions);
    } else {
      setCourts([]);
    }
  };

  const hours = Array.from({ length: 24 }, (_, i) => i);
  const minutes = Array.from({ length: 60 }, (_, i) => i);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!academy || !court || !fromTimeFilter || !toTimeFilter) {
      Swal.fire({
        icon: 'warning',
        title: 'All fields are required!',
        toast: true,
        position: 'top-end',
        timer: 3000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
      return;
    }
    const [startHour, startMinute] = fromTimeFilter.value.split(":").map(Number);
    const [endHour, endMinute] = toTimeFilter.value.split(":").map(Number);
    const istDate = new Date(selectedDate);
    istDate.setHours(istDate.getHours() + 5);
    istDate.setMinutes(istDate.getMinutes() + 30);
    const formattedDate = format(istDate, "yyyy-MM-dd");
    let startISTHour = startHour + 5;
    let startISTMinute = startMinute + 30;
    let startDateShift = 0;
    if (startISTMinute >= 60) {
      startISTHour += 1;
      startISTMinute -= 60;
    }
    if (startISTHour >= 24) {
      startISTHour -= 24;
      startDateShift = 1;
    }
    let endISTHour = endHour + 5;
    let endISTMinute = endMinute + 30;
    let endDateShift = 0;
    if (endISTMinute >= 60) {
      endISTHour += 1;
      endISTMinute -= 60;
    }
    if (endISTHour >= 24) {
      endISTHour -= 24;
      endDateShift = 1;
    }
    const finalStartDate = new Date(selectedDate);
    finalStartDate.setDate(finalStartDate.getDate() + startDateShift);
    const finalEndDate = new Date(selectedDate);
    finalEndDate.setDate(finalEndDate.getDate() + endDateShift);
    const finalFormattedStartDate = format(finalStartDate, "yyyy-MM-dd");
    const finalFormattedEndDate = format(finalEndDate, "yyyy-MM-dd");
    const startTime = `${finalFormattedStartDate}T${String(startISTHour).padStart(2, "0")}:${String(startISTMinute).padStart(2, "0")}:00`;
    const stopTime = `${finalFormattedEndDate}T${String(endISTHour).padStart(2, "0")}:${String(endISTMinute).padStart(2, "0")}:00`;
    const requestData = {
      center: parseInt(academy.value, 10),
      court: parseInt(court.value, 10),
      start: startTime,
      stop: stopTime,
    };
    const newPayload = {
      center: requestData.center,
      centerLabel: academy?.label ?? null,
      court: requestData.court,
      courtLabel: court?.label ?? null,
      start: startTime,
      stop: stopTime,
      selectedDate: format(selectedDate, "yyyy-MM-dd"),
      fromTime: fromTimeFilter?.value ?? null,
      toTime: toTimeFilter?.value ?? null,
    };
    const oldPayload = baselineRef.current || {};
    try {
      const response = await axios.post(
        process.env.REACT_APP_API_ZINCO,
        requestData,
        {
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': process.env.REACT_APP_API_KEY,
          }
        }
      );
      const apiStatus = response.data.externalResponse?.response?.status;
      if (apiStatus === 'success') {
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Message sent successfully!',
          toast: true,
          position: 'top-end',
          timer: 3000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text:  'Failed to send message!',
          toast: true,
          position: 'top-end',
          timer: 3000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }
      const affected_id = `${requestData.center}-${requestData.court}-${startTime}-${stopTime}`;
      fireAudit({
        entity: 'zinco_message',
        affected_id,
        action: 'create',
        section: 'Centre Management',
        subSection: 'Court Lights',
        businessApi: process.env.REACT_APP_API_ZINCO,
        businessMethod: 'POST',
        oldPayload,
        newPayload,
        extra: { page: 'ZincoDashboard' },
      });
      baselineRef.current = newPayload;
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: error.response?.data?.message || 'Failed to send message!',
        toast: true,
        position: 'top-end',
        timer: 3000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    }
  };

  const generateTimeOptions = (openTime, closeTime) => {
    const times = [];
    let startTime = new Date(`1970-01-01T${openTime}:00`);
    const endTime = new Date(`1970-01-01T${closeTime}:00`);
    while (startTime <= endTime) {
      const value = startTime.toTimeString().slice(0, 5);
      const label = startTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
      times.push({ value, label });
      startTime.setMinutes(startTime.getMinutes() + 30);
    }
    return times;
  };

  useEffect(() => {
    if (selectedCenter && academies.length > 0) {
      const defaultAcademy = academies.find(a => a.value == selectedCenter);
      if (defaultAcademy) {
        setAcademy(defaultAcademy);
        const selectedAcademyData = allData.find((item) => item.academy_id == selectedCenter);
        if (selectedAcademyData) {
          const courtNames = selectedAcademyData.court_names.split(",");
          const courtIds = selectedAcademyData.court_ids.split(",");
          const courtOptions = courtNames.map((court, index) => ({
            value: courtIds[index].trim(),
            label: court.trim(),
          }));
          setCourts(courtOptions);
        }
      }
    }
  }, [selectedCenter, academies, allData]);

  return (
    <div>
      <div className="enrollment-container" style={{overflow:'visible'}}>
        <form onSubmit={handleSubmit}>
          <div className="dropdownContainer">
            <label htmlFor="academy">Academy:</label>
            <Select
              id="academy"
              value={academy}
              onChange={handleAcademyChange}
              options={academies}
              placeholder="Select Academy"
              className="dropdown"
              styles={{ menu: (provided) => ({ ...provided, zIndex: 1000 }) }}
            />
          </div>

          <div className="dropdownContainer">
            <label htmlFor="court">Court:</label>
            <Select
              id="court"
              value={court}
              onChange={(selectedOption) => setCourt(selectedOption)}
              options={courts}
              placeholder="Select Court"
              className="dropdown"
              isDisabled={!academy}
              styles={{ menu: (provided) => ({ ...provided, zIndex: 100 }) }}
            />
          </div>
          <div className="datePickerContainer">
            <label htmlFor="datePicker">Select Date:</label>
            <DatePicker
              selected={selectedDate}
              onChange={(date) => setSelectedDate(date)}
              dateFormat="yyyy-MM-dd"
              className="datePicker"
              inline
              minDate={new Date()}
            />
          </div>

          <div className="time-select-containerr">
            <div className="floating-select" style={{ flex: 1 }}>
              <Select
                className={`sales-filter-court-booking ${fromTimeFilter ? "has-value" : ""}`}
                classNamePrefix="react-select"
                name="fromTimeFilter"
                value={fromTimeFilter}
                onChange={setFromTimeFilter}
                options={generateTimeOptions("00:00", "23:30")}
                placeholder="From Time"
                isClearable
                styles={{
                  placeholder: (provided) => ({ ...provided, color: 'black' }),
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
                menuPortalTarget={document.body}
              />
              {fromTimeFilter && <label htmlFor="fromTimeFilter">From Time</label>}
            </div>

            <div className="floating-select" style={{ flex: 1 }}>
              <Select
                className={`sales-filter-court-booking ${toTimeFilter ? "has-value" : ""}`}
                classNamePrefix="react-select"
                name="toTimeFilter"
                value={toTimeFilter}
                onChange={setToTimeFilter}
                options={generateTimeOptions("00:00", "23:30")}
                placeholder="To Time"
                isClearable
                styles={{
                  placeholder: (provided) => ({ ...provided, color: 'black' }),
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
                menuPortalTarget={document.body}
              />
              {toTimeFilter && <label htmlFor="toTimeFilter">To Time</label>}
            </div>
          </div>


            <button type="submit" className="submit-button">
              Submit
            </button>

        </form>
      </div>
    </div>
  );
};

export default ZincoDashboard;
